import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getEnvVar } from "src/utils/get-env";
import { PayPalPayButton } from "../../../../../payments/PaypalPayButton";
import { StripePayButton } from "../../../../../payments/StripePayButton";
import { colors, alphaVar } from "../../../../../ui/colors";
import { IInvoicePaymentMethod } from "../actions/createPaymentOrder";
import { useCreatePaymentOrder } from "../hooks/useCreatePaymentOrder";

interface OnlinePaymentButtonProps {
  gatewayId: string;
  displayName: string;
  totalAmount: number;
  paymentAmount?: number;
  onPaymentClick?: (selectedGatewayId: string) => void;
  buttonDisabled?: boolean;
  onBackClick?: () => void;
  handleChangeStatus?: any;
  onPayPalCaptureSuccess?: () => void;
  paymentMethods?: any;
  invoiceUid?: string;
  invoiceType?: string;
}

export const OnlinePaymentButton = ({
  gatewayId,
  displayName,
  onBackClick,
  handleChangeStatus,
  onPayPalCaptureSuccess,
  invoiceUid,
  invoiceType,
  paymentAmount,
  totalAmount,
}: OnlinePaymentButtonProps) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || sessionStorage.getItem("token");
  const { createPaymentOrder, isCreatingPaymentOrder, isError } = useCreatePaymentOrder();
  const [resolvedMethod, setResolvedMethod] = useState<IInvoicePaymentMethod | null>(null);

  const effectiveAmount = typeof paymentAmount === "number" && paymentAmount > 0 ? paymentAmount : totalAmount;

  useEffect(() => {
    if (!invoiceUid || (gatewayId !== "stripe" && gatewayId !== "paypal")) {
      return;
    }

    let active = true;
    setResolvedMethod(null);

    const initializeOrder = async () => {
      try {
        const result = await createPaymentOrder({
          invoiceUid,
          provider: gatewayId as "stripe" | "paypal",
          amount: effectiveAmount,
          token,
        });

        if (active && result?.paymentMethod) {
          setResolvedMethod(result.paymentMethod);
        }
      } catch (err) {
        console.error(`[CREATE PAYMENT ORDER ERROR for ${gatewayId}]`, err);
      }
    };

    initializeOrder();

    return () => {
      active = false;
    };
  }, [invoiceUid, gatewayId, token, createPaymentOrder, effectiveAmount]);

  const isMerchantConnectedOrder = Boolean(resolvedMethod?.externalMerchantId);
  const appType = isMerchantConnectedOrder ? "merchant" : "platform";

  const resolvedMerchantId =
    resolvedMethod?.externalMerchantId ||
    (appType === "platform" ? getEnvVar("REACT_APP_PAYPAL_PLATFORM_MERCHANT_ID") : getEnvVar("REACT_APP_PAYPAL_MERCHANT_ID")) ||
    undefined;

  const getPaymentButtonMock = () => {
    if (gatewayId === "paypal") {
      return (
        <PayPalPayButton
          handleChangeStatus={handleChangeStatus}
          onCaptureSuccess={onPayPalCaptureSuccess}
          orderId={resolvedMethod?.externalOrderUid}
          merchantId={resolvedMerchantId}
          appType={appType}
        />
      );
    }

    if (gatewayId === "stripe") {
      return <StripePayButton handleChangeStatus={handleChangeStatus} paymentUrl={resolvedMethod?.paymentUrl} invoiceUid={invoiceUid} />;
    }

    return null;
  };

  const handleRetry = async () => {
    try {
      setResolvedMethod(null);
      const result = await createPaymentOrder({
        invoiceUid: invoiceUid || "",
        provider: gatewayId as "stripe" | "paypal",
        amount: effectiveAmount,
        token,
      });
      if (result?.paymentMethod) {
        setResolvedMethod(result.paymentMethod);
      }
    } catch (err) {
      console.error(`[RETRY CREATE PAYMENT ORDER ERROR for ${gatewayId}]`, err);
    }
  };

  const needsOrderCreation = gatewayId === "stripe" || gatewayId === "paypal";

  if (needsOrderCreation && !resolvedMethod) {
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: alphaVar(colors.gray[50], 0.5),
          border: `1px solid ${alphaVar(colors.gray[200], 0.4)}`,
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 180,
        }}
      >
        {onBackClick && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBackClick}
            sx={{
              mb: 2,
              color: colors.gray[600],
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              padding: 0,
              alignSelf: "flex-start",
              "&:hover": {
                bgcolor: "transparent",
                color: colors.orange.base,
              },
            }}
          >
            Back to payment methods
          </Button>
        )}
        <Box sx={{ height: 1, width: "100%", bgcolor: alphaVar(colors.gray[200], 0.3), mb: 2 }} />

        {isError ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 2 }}>
            <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
              Failed to initialize payment method.
            </Typography>
            <Button variant="outlined" onClick={handleRetry} sx={{ textTransform: "none", fontWeight: 700 }}>
              Retry
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ color: colors.gray[500], fontWeight: 500 }}>
              Initializing payment with {displayName}...
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: alphaVar(colors.gray[50], 0.5),
        border: `1px solid ${alphaVar(colors.gray[200], 0.4)}`,
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        width: "100%",
      }}
    >
      {/* Back button */}
      {onBackClick && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBackClick}
          sx={{
            mb: 2,
            color: colors.gray[600],
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            padding: 0,
            alignSelf: "flex-start",
            "&:hover": {
              bgcolor: "transparent",
              color: colors.orange.base,
            },
          }}
        >
          Back to payment methods
        </Button>
      )}

      {/* Divider */}
      <Box sx={{ height: 1, bgcolor: alphaVar(colors.gray[200], 0.3), mb: 2 }} />

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.gray[600], mb: 0.5 }}>
          Pay with {displayName}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.gray[500], fontSize: "0.75rem" }}>
          Complete your payment securely through {displayName}
        </Typography>
      </Box>

      {/* Payment Gateway Mock Button */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>{getPaymentButtonMock()}</Box>

      {/* Info text */}
      <Box sx={{ mt: "auto", pt: 2 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: colors.gray[500],
            fontSize: "0.7rem",
          }}
        >
          You will be redirected to {displayName} to complete your payment securely
        </Typography>
      </Box>
    </Box>
  );
};
